import type {
  PrecompiledSet,
  PrecompiledWeek,
  PrecompiledWeekProgressionRule,
  ProgressionMethodConfig,
  SetItem,
  SogliaItem,
  SogliaThreshold,
} from "../types/Entities";
import {
  computeNextSetInSetLoad,
  computeNextSetInSetReps,
  computeNextWeekLoad,
  computeNextWeekReps,
} from "./progressionEngine";

/**
 * Shared rule-apply helpers for precompiled progressions.
 *
 * Extracted from PrecompiledProgressionForm.tsx and PrecompiledProgressionDetail.tsx
 * (FE-A #a4c1a4b4). Same StS / SiS contract as before, plus a reps-target
 * variant so simulateChain can emit reps projections for fixed_increment /
 * wn_rm rules whose `incrementTarget === "reps"`.
 *
 * `applyRuleStS` / `applyRuleSiS` return null when the fixed-mode rule is
 * silent (none / threshold-driven / reps-target / missing required fields).
 * Threshold mode is handled by `applyRuleThresholdInherit` (FE-C #546b25bd),
 * which delegates to the engine's soglia path via a synthetic SetItem.
 * `computePrevSlotProjection` composes the two: threshold-inherit first, then
 * fixed/wn_rm fallback — matching `computeHypLoad`'s dispatch.
 */

/** Round a number to 2 decimals (mirrors progressionEngine 2-decimal contract). */
const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * FE-P (#0370f830) — paste-time normalizer for a copied PrecompiledSet's
 * progression rule. Set copy/paste preserves thresholds end-to-end via
 * deep-clone, but the form's render guards hide the threshold rows when the
 * gating fields (`incrementTarget` / `loadChangeType` and the SiS mirrors)
 * are null. A pasted rule may legitimately have thresholds populated while
 * a gating field is unset — e.g. the set was edited in an older revision
 * before the per-tab `incrementTarget(SiS)` field existed, or the user
 * nulled `loadChangeType` mid-edit.
 *
 * This helper back-fills sensible defaults so the threshold rows render.
 * It NEVER overwrites already-set fields, so the common case (a clean rule
 * on the clipboard) is a no-op.
 *
 * Heuristics:
 * - If thresholds (positive OR negative) are non-empty AND `incrementTarget`
 *   is null → infer "reps" if rows have only repsValue, "both" if both
 *   loadValue and repsValue appear, else "load".
 * - If the resulting target needs `loadChangeType` (load/both) and that
 *   field is null → default to "kg".
 * - Mirror for SiS.
 *
 * Mutation is in-place on the passed object — callers should pass a deep
 * clone, not the source.
 */
export function normalizePastedRule(
  rule: PrecompiledWeekProgressionRule,
): PrecompiledWeekProgressionRule {
  const inferTarget = (
    pos: SogliaThreshold[] | null | undefined,
    neg: SogliaThreshold[] | null | undefined,
  ): "load" | "reps" | "both" | null => {
    const all = [...(pos ?? []), ...(neg ?? [])];
    if (all.length === 0) return null;
    const hasReps = all.some(
      (t) => t.repsValue !== null && t.repsValue !== undefined && String(t.repsValue) !== "",
    );
    const hasLoad = all.some((t) => t.loadValue !== null && t.loadValue !== undefined);
    if (hasReps && hasLoad) return "both";
    if (hasReps) return "reps";
    return "load";
  };

  const stsThresholds = (rule.positiveThresholdsStS?.length ?? 0) > 0
    || (rule.negativeThresholdsStS?.length ?? 0) > 0;
  if (stsThresholds && rule.incrementTarget == null) {
    rule.incrementTarget = inferTarget(rule.positiveThresholdsStS, rule.negativeThresholdsStS) ?? "load";
  }
  if (
    stsThresholds
    && (rule.incrementTarget === "load" || rule.incrementTarget === "both")
    && rule.loadChangeType == null
  ) {
    rule.loadChangeType = "kg";
  }

  const sisThresholds = (rule.positiveThresholdsSiS?.length ?? 0) > 0
    || (rule.negativeThresholdsSiS?.length ?? 0) > 0;
  if (sisThresholds && rule.incrementTargetSiS == null) {
    rule.incrementTargetSiS = inferTarget(rule.positiveThresholdsSiS, rule.negativeThresholdsSiS) ?? "load";
  }
  if (
    sisThresholds
    && (rule.incrementTargetSiS === "load" || rule.incrementTargetSiS === "both")
    && rule.loadChangeTypeSiS == null
  ) {
    rule.loadChangeTypeSiS = "kg";
  }

  return rule;
}

/**
 * Per-box Copia/Incolla for the StS / SiS progression-method panels.
 *
 * The two boxes carry the same conceptual config but on differently-suffixed
 * rule fields (StS on the bare names, SiS on the `*SiS` mirrors). The four
 * helpers below map a rule's StS / SiS fields to/from the canonical
 * suffix-free `ProgressionMethodConfig` so a config copied from one box can be
 * pasted into either box.
 *
 * `extract*` deep-clones the threshold arrays so the clipboard payload never
 * shares references with the live rule. `apply*` returns a `Partial` suitable
 * for the editor's `onChange({ ...rule, ...partial })` contract and likewise
 * clones the thresholds so two paste targets don't alias one clipboard entry.
 */
const cloneThresholds = (
  ts: SogliaThreshold[] | null | undefined,
): SogliaThreshold[] | null =>
  ts == null ? null : ts.map((t) => ({ ...t }));

export function extractStSConfig(
  rule: PrecompiledWeekProgressionRule,
): ProgressionMethodConfig {
  return {
    type: rule.type ?? "fixed",
    applyFor: rule.applyForNextWeeks ?? null,
    weekPriority: rule.weekPriority ?? null,
    incrementDirection: rule.incrementDirection ?? null,
    incrementTarget: rule.incrementTarget ?? null,
    repsChange: rule.repsChange ?? null,
    loadChangeType: rule.loadChangeType ?? null,
    loadChangeValue: rule.loadChangeValue ?? null,
    weekWnRm: rule.weekWnRm ?? null,
    weekPnRm: rule.weekPnRm ?? null,
    matrixSource: rule.matrixSource ?? null,
    positiveThresholds: cloneThresholds(rule.positiveThresholdsStS),
    negativeThresholds: cloneThresholds(rule.negativeThresholdsStS),
  };
}

export function extractSiSConfig(
  rule: PrecompiledWeekProgressionRule,
): ProgressionMethodConfig {
  // SiS reads the `*SiS` fields, falling back to the bare StS-shared fields
  // for `type` / `incrementTarget` (back-compat — same fallbacks the editor
  // uses at its SiS render site).
  return {
    type: rule.typeSiS ?? rule.type ?? "fixed",
    applyFor: rule.applyForNextSets ?? null,
    weekPriority: rule.weekPrioritySiS ?? null,
    incrementDirection: rule.incrementDirectionSiS ?? null,
    incrementTarget: rule.incrementTargetSiS ?? rule.incrementTarget ?? null,
    repsChange: rule.repsChangeSiS ?? null,
    loadChangeType: rule.loadChangeTypeSiS ?? null,
    loadChangeValue: rule.loadChangeValueSiS ?? null,
    weekWnRm: rule.weekWnRmSiS ?? null,
    weekPnRm: rule.weekPnRmSiS ?? null,
    matrixSource: rule.matrixSourceSiS ?? null,
    positiveThresholds: cloneThresholds(rule.positiveThresholdsSiS),
    negativeThresholds: cloneThresholds(rule.negativeThresholdsSiS),
  };
}

export function applyStSConfig(
  config: ProgressionMethodConfig,
): Partial<PrecompiledWeekProgressionRule> {
  return {
    type: config.type,
    applyForNextWeeks: config.applyFor,
    weekPriority: config.weekPriority,
    incrementDirection: config.incrementDirection,
    incrementTarget: config.incrementTarget,
    repsChange: config.repsChange,
    loadChangeType: config.loadChangeType,
    loadChangeValue: config.loadChangeValue,
    weekWnRm: config.weekWnRm,
    weekPnRm: config.weekPnRm,
    matrixSource: config.matrixSource,
    positiveThresholdsStS: cloneThresholds(config.positiveThresholds),
    negativeThresholdsStS: cloneThresholds(config.negativeThresholds),
  };
}

export function applySiSConfig(
  config: ProgressionMethodConfig,
): Partial<PrecompiledWeekProgressionRule> {
  return {
    typeSiS: config.type,
    applyForNextSets: config.applyFor,
    weekPrioritySiS: config.weekPriority,
    incrementDirectionSiS: config.incrementDirection,
    incrementTargetSiS: config.incrementTarget,
    repsChangeSiS: config.repsChange,
    loadChangeTypeSiS: config.loadChangeType,
    loadChangeValueSiS: config.loadChangeValue,
    weekWnRmSiS: config.weekWnRm,
    weekPnRmSiS: config.weekPnRm,
    matrixSourceSiS: config.matrixSource,
    positiveThresholdsSiS: cloneThresholds(config.positiveThresholds),
    negativeThresholdsSiS: cloneThresholds(config.negativeThresholds),
  };
}

/**
 * PROG-ENGINE-V2/3 (Bug 3) — derive `loadComputeSource` from `progressionType`
 * when the rule's explicit `loadComputeSource` is null. Mirrors what the BE
 * normalizer produces on save, so the projection grid + per-set badge render
 * immediately after configuration without waiting for save+reopen.
 *
 * "set-to-set" → "sts", "set-in-set" → "sis", "both" → "both". Any other
 * progressionType (incl. "none", null/undefined) returns null so the caller's
 * switch falls into its default branch unchanged.
 *
 * Explicit `loadComputeSource` always wins — this is a fallback, not an
 * override.
 */
export function effectiveLoadComputeSource(
  rule: PrecompiledWeekProgressionRule,
): PrecompiledWeekProgressionRule["loadComputeSource"] {
  if (rule.loadComputeSource != null) return rule.loadComputeSource;
  switch (rule.progressionType) {
    case "set-to-set": return "sts";
    case "set-in-set": return "sis";
    case "both": return "both";
    default: return null;
  }
}

/**
 * FE-J (#9b57b1a4) — first-week + first-set baseline. Resolves the initial
 * load purely from `initialLoadType` / `initialLoad` / `initialWnRm` /
 * `initialPnRm`, independently of the forward `rule.type`. Previously the
 * page-level computeHypLoad gated this branch on `rule.type === "fixed"`,
 * which left thresholds-mode source sets with `null` baselines on cold
 * render and starved downstream computePrevSlotProjection of `prevHypLoad`.
 */
export function computeInitialBaseline(
  rule: PrecompiledWeekProgressionRule,
): number | null {
  if (rule.initialLoadType === "fixed" || rule.initialLoadType == null)
    return rule.initialLoad ?? null;
  if (rule.initialLoadType === "dynamic" && rule.initialWnRm != null && rule.initialPnRm != null)
    return Math.round((rule.initialWnRm * rule.initialPnRm) / 100 * 100) / 100;
  return null;
}

/**
 * Apply the StS load rule and return the projected load, or null if the rule
 * is silent (weekPriority "none" / threshold-driven / reps-target / missing
 * inputs / not a load-targeted increment).
 */
export function applyRuleStS(
  baseLoad: number,
  rule: PrecompiledWeekProgressionRule,
): number | null {
  if (rule.weekPriority === "wn_rm" && rule.weekWnRm != null && rule.weekPnRm != null) {
    return round2((rule.weekWnRm * rule.weekPnRm) / 100);
  }
  if (
    rule.weekPriority === "fixed_increment" &&
    rule.incrementTarget !== "reps" &&
    rule.loadChangeValue != null
  ) {
    const sign = rule.incrementDirection === "decrement" ? -1 : 1;
    // #dcf0ceef — treat a missing loadChangeType as "kg" (the form UI's
    // visual default via `effectiveUnit = loadChangeType ?? "kg"`).
    // Defensive backstop for plans saved before the FE-side seed: previously
    // a strict `=== "kg"` / `=== "percentage"` check returned null when the
    // unit was unset, starving the grid for any downstream StS-inherit slot.
    const unit = rule.loadChangeType ?? "kg";
    if (unit === "kg") {
      return round2(baseLoad + sign * rule.loadChangeValue);
    }
    if (unit === "percentage") {
      return round2(baseLoad * (1 + (sign * rule.loadChangeValue) / 100));
    }
  }
  return null;
}

/**
 * Apply the SiS load rule and return the projected load, or null when silent.
 * Mirrors applyRuleStS, reading the SiS-suffixed fields.
 */
export function applyRuleSiS(
  baseLoad: number,
  rule: PrecompiledWeekProgressionRule,
): number | null {
  if (
    rule.weekPrioritySiS === "wn_rm" &&
    rule.weekWnRmSiS != null &&
    rule.weekPnRmSiS != null
  ) {
    return round2((rule.weekWnRmSiS * rule.weekPnRmSiS) / 100);
  }
  if (
    rule.weekPrioritySiS === "fixed_increment" &&
    rule.incrementTargetSiS !== "reps" &&
    rule.loadChangeValueSiS != null
  ) {
    const sign = rule.incrementDirectionSiS === "decrement" ? -1 : 1;
    // #dcf0ceef — mirror of applyRuleStS: treat a missing loadChangeTypeSiS
    // as "kg" so plans saved before the FE-side seed still project.
    const unit = rule.loadChangeTypeSiS ?? "kg";
    if (unit === "kg") {
      return round2(baseLoad + sign * rule.loadChangeValueSiS);
    }
    if (unit === "percentage") {
      return round2(baseLoad * (1 + (sign * rule.loadChangeValueSiS) / 100));
    }
  }
  return null;
}

/**
 * FE-F (#a2bde480) — REPLACE semantics: `repsChange` is the new absolute reps
 * target, not a delta. Returns the configured value verbatim — numbers pass
 * through; strings matching `^\d+(\s*[-–]\s*\d+)?$` (single integer or range
 * with hyphen / en-dash / inner whitespace) are returned trimmed; otherwise
 * returns null. `incrementDirection` is irrelevant on the reps target —
 * direction is a load-only concept. wn_rm has no reps semantics, so it's
 * silent here.
 *
 * The `_baseReps` parameter is intentionally NOT consumed: it is kept in the
 * signature for callsite symmetry with the load helpers and so that the
 * existing simulateChain plumbing in `precompiledChain.ts` doesn't change.
 */
const REPS_TARGET_PATTERN = /^\d+(\s*[-–]\s*\d+)?$/;

function coerceRepsTarget(v: number | string | null | undefined): number | string | null {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) && v > 0 ? v : null;
  const trimmed = v.trim();
  if (!REPS_TARGET_PATTERN.test(trimmed)) return null;
  return trimmed;
}

export function applyRuleStSReps(
  _baseReps: number,
  rule: PrecompiledWeekProgressionRule,
): number | string | null {
  if (rule.weekPriority !== "fixed_increment") return null;
  if (rule.incrementTarget !== "reps" && rule.incrementTarget !== "both") return null;
  return coerceRepsTarget(rule.repsChange);
}

/** SiS-side mirror of applyRuleStSReps. */
export function applyRuleSiSReps(
  _baseReps: number,
  rule: PrecompiledWeekProgressionRule,
): number | string | null {
  if (rule.weekPrioritySiS !== "fixed_increment") return null;
  if (rule.incrementTargetSiS !== "reps" && rule.incrementTargetSiS !== "both") return null;
  return coerceRepsTarget(rule.repsChangeSiS);
}

/**
 * PROG-ENGINE-V2/4 (Area 1 Strategy A, plan #72a6c050 §2) — "completed slot"
 * predicate. A prev slot counts as completed once the athlete has logged ANY
 * work data — either workReps or workLoad. This matches the §2 spec exactly
 * so the inheritance path stays deterministic across the StS / SiS targets.
 */
function isPrevSlotCompleted(prevSet: PrecompiledSet | null | undefined): boolean {
  if (!prevSet) return false;
  if (prevSet.workReps != null && String(prevSet.workReps).trim() !== "") return true;
  if (prevSet.workLoad != null) return true;
  return false;
}

/**
 * PROG-ENGINE-V2/4 (Area 1 Strategy A, plan #72a6c050 §2). Adapter that lets
 * the threshold-inherit helpers prefer *configured* values when the prev slot
 * is NOT completed (no `workReps` / `workLoad` logged yet).
 *
 * - Completed prev set → returns `{ workReps, workLoad, fromExecution: true }`
 *   verbatim. Existing behavior preserved (no regression on the post-execution
 *   path that drives the original Wave 3.1 inheritance).
 * - Uncompleted prev set → returns `{ workReps: configured repScheme,
 *   workLoad: prevHypLoad, fromExecution: false }`. The configured reps stay
 *   as a possibly-range string ("8-12") because, without an executed reps
 *   count, the engine threshold path can't meaningfully pick positive vs.
 *   negative — callers route this through the in-range carryover branch
 *   instead, mirroring PROG-ENGINE-V2/2's "configured wins" semantics.
 *
 * Returns `null` when the slot is uncompleted AND no `prevHypLoad` is
 * available, so legacy 3-arg callers (and the page-level callers that don't
 * yet thread prevHypLoad through to the helpers) short-circuit unchanged.
 */
export interface MaterializedPrevSlot {
  workReps: number | string | null;
  workLoad: number | null;
  /** True iff the slot has logged work data — gates engine vs carryover. */
  fromExecution: boolean;
}

export function materializePrevSlot(
  prevSet: PrecompiledSet | null | undefined,
  prevHypLoad: number | null | undefined,
  weekRepScheme: string | null | undefined,
): MaterializedPrevSlot | null {
  if (!prevSet) return null;
  if (isPrevSlotCompleted(prevSet)) {
    return {
      workReps: prevSet.workReps ?? null,
      workLoad: prevSet.workLoad ?? null,
      fromExecution: true,
    };
  }
  const configuredReps = prevSet.repScheme ?? weekRepScheme ?? null;
  if (prevHypLoad == null) return null;
  return {
    workReps: configuredReps,
    workLoad: prevHypLoad,
    fromExecution: false,
  };
}

/**
 * Wave 3.1 (#583bd67b) — threshold-aware inheritance helper. When the previous
 * slot's rule.type === "thresholds" AND has persisted workReps + workLoad
 * (Wave 1.3), project via the engine's threshold path. `target` selects StS
 * vs SiS branch. Extracted from PrecompiledProgressionForm.tsx /
 * PrecompiledProgressionDetail.tsx in FE-C (#546b25bd).
 */
export function applyRuleThresholdInherit(
  prevSet: PrecompiledSet,
  weekRepScheme: string | null | undefined,
  target: "sts" | "sis",
  // PROG-ENGINE-V2/4 (#f38a51f4) — optional projected baseline. When supplied,
  // an uncompleted prev slot materializes through `materializePrevSlot` so
  // the inheritance reads the engine's projected load instead of returning
  // null. Defaults to undefined for back-compat with the 3-arg callers.
  prevHypLoad?: number | null,
): number | null {
  const rule = prevSet.progressionRule;
  if (!rule || rule.type !== "thresholds") return null;
  // Per Wave 2.1: SiS resolves typeSiS ?? type. If user picked "fixed" for SiS,
  // the threshold-inherit path doesn't apply on that target.
  const ruleKind = target === "sis" ? (rule.typeSiS ?? rule.type) : rule.type;
  if (ruleKind !== "thresholds") return null;
  // PROG-ENGINE-V2/4 — materialize prev slot. Completed → verbatim work data;
  // uncompleted → configured repScheme + prevHypLoad. Without prevHypLoad on
  // an uncompleted slot, the materializer returns null and we preserve the
  // pre-#f38a51f4 short-circuit behavior (legacy callers unaffected).
  const mat = materializePrevSlot(prevSet, prevHypLoad, weekRepScheme);
  if (!mat) return null;
  const wr = mat.workReps != null ? parseFloat(String(mat.workReps)) : NaN;
  const wl = mat.workLoad != null ? Number(mat.workLoad) : NaN;
  if (isNaN(wr) || isNaN(wl) || wr <= 0 || wl <= 0) return null;
  // Uncompleted slot: skip the engine (no executed reps to evaluate
  // thresholds against — parseFloat of a range gives just the low bound,
  // which would silently bias toward the negative side). Carry the
  // materialized workLoad (== prevHypLoad) forward as the suggested load.
  if (!mat.fromExecution) return wl;
  const lct: "%" | "weight" =
    (target === "sis" ? rule.loadChangeTypeSiS : rule.loadChangeType) === "kg"
      ? "weight"
      : "%";
  const toSoglie = (ts: SogliaThreshold[] | null | undefined): SogliaItem[] =>
    (ts ?? []).map((th) => ({ threshold: th.threshold, type: lct, value: th.loadValue ?? 0 }));
  const synth: SetItem =
    target === "sis"
      ? {
          intensityType: "%",
          progressionType: "set-in-set",
          calculationPriority: "soglie",
          workLoad: String(wl),
          repScheme: (prevSet.repScheme ?? weekRepScheme) ?? undefined,
          incrementRuleSiS: { type: lct, value: 0, soglie: toSoglie(rule.positiveThresholdsSiS) },
          decrementRuleSiS: { type: lct, value: 0, soglie: toSoglie(rule.negativeThresholdsSiS) },
        }
      : {
          intensityType: "%",
          progressionType: "set-to-set",
          calculationPriority: "soglie",
          workLoad: String(wl),
          repScheme: (prevSet.repScheme ?? weekRepScheme) ?? undefined,
          incrementRuleStS: { type: lct, value: 0, soglie: toSoglie(rule.positiveThresholdsStS) },
          decrementRuleStS: { type: lct, value: 0, soglie: toSoglie(rule.negativeThresholdsStS) },
        };
  const projected =
    target === "sis"
      ? computeNextSetInSetLoad(synth, wr)
      : computeNextWeekLoad(synth, wr);
  // PROG-BUGS/ENGINE-1 (Bug C) — when no soglia matches (athlete stays
  // in-range, no progression fires), the engine returns undefined. Carry
  // the previous set's `workLoad` forward instead of returning null, so
  // the next slot in the same position keeps the same suggested load.
  // `wl` is guaranteed positive here (validated at line 244-245).
  if (projected != null) return projected;
  return wl > 0 ? wl : null;
}

/**
 * Map a precompiled rule's threshold sub-arrays onto engine `SogliaItem[]`,
 * honoring the per-tab `loadChangeType` / `loadChangeTypeSiS`.
 *
 * Moved from `precompiledChain.ts` (PROG-INT/B #a37befa5) so the precompiled →
 * Programmazione adapter can reuse it without depending on the chain module.
 */
export function thresholdsToSoglie(
  thresholds: SogliaThreshold[] | null | undefined,
  loadType: "%" | "weight",
): SogliaItem[] {
  return (thresholds ?? []).map((t) => ({
    threshold: t.threshold,
    type: loadType,
    value: t.loadValue ?? 0,
  }));
}

/**
 * FE-2 (#bf6553b3) — map threshold rows' `repsValue` onto `repScheme`-typed
 * engine soglie. Skips rows where repsValue is null/empty so the engine sees
 * only actionable rules. Numeric repsValue passes through verbatim (engine
 * emits {kind:"reps"}); string repsValue passes through unmodified so the
 * engine's range-aware path (FE-1 `getSogliaSchemeValue`) can match "8-10".
 *
 * Moved from `precompiledChain.ts` to this shared util in FE-H (#130311b7)
 * so `computePrevSlotProjection`'s reps branch can reuse the same adapter.
 */
export function repsThresholdsToSoglie(
  thresholds: SogliaThreshold[] | null | undefined,
): SogliaItem[] {
  return (thresholds ?? [])
    .filter((t) => t.repsValue !== null && t.repsValue !== undefined && String(t.repsValue) !== "")
    .map((t) => ({
      threshold: t.threshold,
      type: "repScheme" as const,
      value: typeof t.repsValue === "number" ? t.repsValue : String(t.repsValue ?? ""),
    }));
}

/**
 * FE-H (#130311b7) — threshold-aware reps inheritance helper. When the prev
 * slot's rule is thresholds AND `incrementTarget(SiS)` includes "reps", build
 * a synthetic SetItem with the prev set's persisted workReps/workLoad and
 * route through the engine's `computeNextWeekReps` / `computeNextSetInSetReps`
 * to pick up FE-1/FE-2's range-string semantics.
 */
export function applyRuleThresholdInheritReps(
  prevSet: PrecompiledSet,
  weekRepScheme: string | null | undefined,
  target: "sts" | "sis",
  // PROG-ENGINE-V2/4 (#f38a51f4) — optional projected baseline (mirror of
  // applyRuleThresholdInherit's 4th arg). Lets uncompleted prev slots resolve
  // their reps inheritance from the configured repScheme (per AC1.3).
  prevHypLoad?: number | null,
): number | string | null {
  const rule = prevSet.progressionRule;
  if (!rule || rule.type !== "thresholds") return null;
  const ruleKind = target === "sis" ? (rule.typeSiS ?? rule.type) : rule.type;
  if (ruleKind !== "thresholds") return null;
  const incTarget = target === "sis" ? rule.incrementTargetSiS : rule.incrementTarget;
  if (incTarget !== "reps" && incTarget !== "both") return null;
  // PROG-ENGINE-V2/4 — completed prev slot uses the engine path verbatim
  // (Wave 3.1 behavior preserved). Uncompleted prev slot surfaces the
  // configured repScheme directly per AC1.3, since without an executed reps
  // value the engine threshold path can't meaningfully pick positive vs.
  // negative. The reps fallback doesn't need `prevHypLoad` — repScheme is
  // a per-set / week-level configured value already on the prevSet — so this
  // branch fires even when no prevHypLoad is threaded in (e.g. legacy 3-arg
  // callers in this same module's computeHypReps).
  if (!isPrevSlotCompleted(prevSet)) {
    const sourceScheme = prevSet.repScheme ?? weekRepScheme ?? null;
    return stripSetCountPrefix(sourceScheme);
  }
  // Completed prev: existing materializer + engine path. prevHypLoad is
  // unused for completed slots since workLoad is the executed value.
  const mat = materializePrevSlot(prevSet, prevHypLoad, weekRepScheme);
  if (!mat) return null;
  const wr = mat.workReps != null ? parseFloat(String(mat.workReps)) : NaN;
  const wl = mat.workLoad != null ? Number(mat.workLoad) : NaN;
  if (isNaN(wr) || isNaN(wl) || wr <= 0 || wl <= 0) return null;
  const positive =
    target === "sis" ? rule.positiveThresholdsSiS : rule.positiveThresholdsStS;
  const negative =
    target === "sis" ? rule.negativeThresholdsSiS : rule.negativeThresholdsStS;
  const synth: SetItem =
    target === "sis"
      ? {
          intensityType: "%",
          progressionType: "set-in-set",
          calculationPriority: "soglie",
          workLoad: String(wl),
          repScheme: (prevSet.repScheme ?? weekRepScheme) ?? undefined,
          incrementRuleSiS: { type: "repScheme", value: 0, soglie: repsThresholdsToSoglie(positive) },
          decrementRuleSiS: { type: "repScheme", value: 0, soglie: repsThresholdsToSoglie(negative) },
        }
      : {
          intensityType: "%",
          progressionType: "set-to-set",
          calculationPriority: "soglie",
          workLoad: String(wl),
          repScheme: (prevSet.repScheme ?? weekRepScheme) ?? undefined,
          incrementRuleStS: { type: "repScheme", value: 0, soglie: repsThresholdsToSoglie(positive) },
          decrementRuleStS: { type: "repScheme", value: 0, soglie: repsThresholdsToSoglie(negative) },
        };
  const projected =
    target === "sis"
      ? computeNextSetInSetReps(synth, wr)
      : computeNextWeekReps(synth, wr);
  if (projected) {
    if (projected.kind === "scheme") return projected.repScheme;
    return projected.reps;
  }
  // PROG-ENGINE-V2/2 (#eef2cdfa) — Bug 2 fix.
  //
  // When no soglia matches (athlete stayed in-range, no progression fires)
  // the engine returns undefined. Carry the *configured* repScheme forward,
  // NOT the executed `workReps`: a prescription of "8-12" with an executed
  // 10 reps must remain "8-12" for the next slot, otherwise an in-range hit
  // would silently collapse the band to the single executed value.
  //
  // Asymmetry note vs. the load branch (`applyRuleThresholdInherit` at
  // L282-283): the load branch intentionally carries `workLoad` (executed
  // kg) because the executed weight IS the meaningful inheritance baseline
  // for the next load progression. Reps are a target band, not a baseline.
  //
  // Source priority mirrors the synthetic `repScheme` above (L358 / L367):
  // per-set first, then week-level fallback. Strip the leading "\dx"
  // set-count prefix so a week-level "4x8-12" surfaces as "8-12" (matches
  // the `hypRepsDisplay` helper used by the read-only header pill).
  const sourceScheme = prevSet.repScheme ?? weekRepScheme ?? null;
  return stripSetCountPrefix(sourceScheme);
}

/**
 * FE-C (#546b25bd) — full per-target previous-slot projection used by the
 * "Calcolo Carico di Questo Set" panel and computeHypLoad. Mirrors the
 * stsInherit/sisInherit logic in computeHypLoad: threshold path first, then
 * fall back to fixed/wn_rm via applyRuleStS/applyRuleSiS.
 *
 * FE-H (#130311b7) — extended return shape to `{load, reps}` so the panel can
 * surface both "carico ipotetico" and "reps ipotetiche" badges. Returns null
 * (the whole shape) when both branches resolve to null AND no prev set/rule
 * was supplied.
 */
export interface PrevSlotProjectionInput {
  target: "sts" | "sis";
  prevWeekSet?: PrecompiledSet | null;
  prevSetInWeek?: PrecompiledSet | null;
  prevWeekHypLoad?: number | null;
  prevWeekSetRule?: PrecompiledWeekProgressionRule | null;
  prevSetHypLoad?: number | null;
  prevSetRule?: PrecompiledWeekProgressionRule | null;
  weekRepScheme?: string | null;
}

export interface PrevSlotProjection {
  load: number | null;
  reps: number | string | null;
}

export function computePrevSlotProjection(
  input: PrevSlotProjectionInput,
): PrevSlotProjection | null {
  const {
    target,
    prevWeekSet,
    prevSetInWeek,
    prevWeekHypLoad,
    prevWeekSetRule,
    prevSetHypLoad,
    prevSetRule,
    weekRepScheme,
  } = input;
  const prevSet = target === "sts" ? prevWeekSet : prevSetInWeek;
  const prevHypLoad = target === "sts" ? prevWeekHypLoad : prevSetHypLoad;
  const prevRule = target === "sts" ? prevWeekSetRule : prevSetRule;
  // PROG-ENGINE-V3/6 (#4b4ff1bd) — additive bootstrap. When the upstream
  // chain failed to produce a prevHypLoad for the source slot (typical
  // first-week first-set scenario where the trainer configured only the
  // FORWARD rule and left initialLoad blank), seed prevHypLoad from
  // prevSet's authoritative load fields. Priority mirrors Step A's
  // `synthPrev` walk-back exactly: workLoad (executed) → initialLoad
  // (configured baseline) → trialLoad (tentative). Purely additive: only
  // fires when prevHypLoad is null AND a prevSet exists. Existing callers
  // that already supplied a non-null prevHypLoad are unaffected.
  let effectivePrevHypLoad: number | null = prevHypLoad ?? null;
  if (effectivePrevHypLoad == null && prevSet != null) {
    effectivePrevHypLoad =
      prevSet.workLoad ??
      prevSet.progressionRule?.initialLoad ??
      prevSet.trialLoad ??
      null;
  }
  // The prev rule's incrementTarget gates whether load/reps fire. Default
  // "load" preserves legacy behavior when the field is unset.
  const effectiveRule = prevRule ?? prevSet?.progressionRule ?? null;
  const incTarget = effectiveRule
    ? (target === "sts" ? effectiveRule.incrementTarget : effectiveRule.incrementTargetSiS) ?? "load"
    : "load";
  const wantsLoad = incTarget === "load" || incTarget === "both";
  const wantsReps = incTarget === "reps" || incTarget === "both";

  // Load resolution: threshold-inherit first, then fixed/wn_rm fallback.
  // PROG-ENGINE-V2/4 — thread prevHypLoad into the inherit helpers so that
  // an uncompleted prev slot materializes through the projected baseline
  // (instead of returning null and falling all the way back to fixed/wn_rm).
  let load: number | null = null;
  if (wantsLoad) {
    if (prevSet) {
      load = applyRuleThresholdInherit(prevSet, weekRepScheme, target, effectivePrevHypLoad);
    }
    if (load == null && effectivePrevHypLoad != null && prevRule != null) {
      load = target === "sts"
        ? applyRuleStS(effectivePrevHypLoad, prevRule)
        : applyRuleSiS(effectivePrevHypLoad, prevRule);
    }
  }
  // Reps resolution: thresholds-via-engine first (FE-H), then FE-F fixed_increment REPLACE.
  let reps: number | string | null = null;
  if (wantsReps) {
    if (prevSet) {
      reps = applyRuleThresholdInheritReps(prevSet, weekRepScheme, target, effectivePrevHypLoad);
    }
    if (reps == null && prevRule != null) {
      reps = target === "sts"
        ? applyRuleStSReps(0, prevRule)
        : applyRuleSiSReps(0, prevRule);
    }
  }
  if (load == null && reps == null && !prevSet && prevRule == null) return null;
  return { load, reps };
}

// ─── PROG-BUGS/ENGINE-2 (Bug B) — `applyForNext*` walk-back ──────────────────
//
// Architect plan #5f4be964 §1.B. The `applyForNextWeeks` / `applyForNextSets`
// fields on PrecompiledWeekProgressionRule (types/Entities.ts:271-273) are
// captured by the form but were previously ignored by the engine
// (precompiledChain.ts comment: "applyForNext walk-back is OUT OF SCOPE for
// this wave"). Result: only the immediate next slot saw the source rule;
// iterations 2..N showed nothing on the hyp-load grid and emitted no chain
// projection.
//
// `resolveEffectiveRule(weeks, wi, si, axis)` walks back along the requested
// axis to find the most recent ancestor whose `applyForNextWeeks` /
// `applyForNextSets` covers the current offset, and returns the effective
// rule + its source coordinates. When the current slot has its own rule (any
// dispatch-driving field set), that rule wins — walk-back is only consulted
// for empty slots. Walk-back stops at the first non-covering own-ruled slot
// (the chain is broken).
//
// Semantics:
// - axis="sts": walk weeks[wi-1].sets[si], weeks[wi-2].sets[si], … (same SiS
//   position, earlier week). `applyForNextWeeks` on rule at (sourceWi, si)
//   covers offsets 1..applyForNextWeeks; current offset = wi - sourceWi.
// - axis="sis": walk weeks[wi].sets[si-1], weeks[wi].sets[si-2], …
//   `applyForNextSets` covers offsets 1..applyForNextSets; current offset =
//   si - sourceSi.
// - null/missing `applyForNext*` is treated as 1 (legacy: rule applies to
//   one subsequent slot only).
// - Closest source wins (first hit while walking back).
// - "Both" rules with both fields set: each axis walks independently — the
//   caller invokes resolveEffectiveRule once per axis.

/**
 * True when the rule carries any dispatch-driving field (i.e. the trainer
 * has actually configured something on this slot). Used to distinguish a
 * truly empty `emptyRule()` shape from a slot the trainer has touched.
 * `type` is mandatory ("fixed" by default), so it can't be used as the
 * "is empty" signal on its own.
 */
export function ruleHasOwnContent(rule: PrecompiledWeekProgressionRule | null | undefined): boolean {
  if (!rule) return false;
  if (rule.weekPriority != null) return true;
  if (rule.weekPrioritySiS != null) return true;
  if (rule.loadComputeSource != null) return true;
  if (rule.initialLoad != null) return true;
  if (rule.initialWnRm != null || rule.initialPnRm != null) return true;
  if (rule.progressionType != null) return true;
  if ((rule.positiveThresholdsStS?.length ?? 0) > 0) return true;
  if ((rule.negativeThresholdsStS?.length ?? 0) > 0) return true;
  if ((rule.positiveThresholdsSiS?.length ?? 0) > 0) return true;
  if ((rule.negativeThresholdsSiS?.length ?? 0) > 0) return true;
  return false;
}

// PROG-ENGINE-V3/1 — walk-back termination predicate. A slot whose only
// content is `loadComputeSource` (a *consumer* of inheritance, not a
// *producer*) must NOT terminate walk-back: AC1.4 requires (w2,s0) with
// `loadComputeSource:"sts"` only to keep walking past (w1,s0) (also bare
// loadComputeSource) to find (w0,s0)'s rule body. ruleHasOwnContent stays
// unchanged so the dispatch-site `ruleIsEmpty` check at the slot's own rule
// continues to reflect "trainer has touched this slot".
export function ruleHasInheritableBody(rule: PrecompiledWeekProgressionRule | null | undefined): boolean {
  if (!rule) return false;
  if (rule.weekPriority != null) return true;
  if (rule.weekPrioritySiS != null) return true;
  if (rule.initialLoad != null) return true;
  if (rule.initialWnRm != null || rule.initialPnRm != null) return true;
  if (rule.progressionType != null) return true;
  if ((rule.positiveThresholdsStS?.length ?? 0) > 0) return true;
  if ((rule.negativeThresholdsStS?.length ?? 0) > 0) return true;
  if ((rule.positiveThresholdsSiS?.length ?? 0) > 0) return true;
  if ((rule.negativeThresholdsSiS?.length ?? 0) > 0) return true;
  return false;
}

export interface EffectiveRuleResolution {
  rule: PrecompiledWeekProgressionRule;
  sourceWi: number;
  sourceSi: number;
}

/**
 * PROG-BUGS/ENGINE-2 — find the multi-iter source rule covering (wi, si) on
 * the requested axis. See block comment above for semantics.
 *
 * PROG-ENGINE-V2/1 (#24b8138a) — Bug 1 fix: an intermediate own-ruled hop
 * whose own `applyForNext*` window does NOT cover the current offset must
 * NOT break the walk-back. Older ancestors with a wider window can still
 * legitimately cover (wi, si) and must be returned. Coverage
 * (`offset <= apply`) is the sole termination condition; non-covering
 * own-ruled hops are skipped, not treated as chain-breakers.
 *
 * Returns null when:
 * - The current slot is the source (no walk-back needed; caller should use
 *   the slot's own rule directly).
 * - No covering source exists within walk-back range.
 */
export function resolveEffectiveRule(
  weeks: PrecompiledWeek[],
  wi: number,
  si: number,
  axis: "sts" | "sis",
): EffectiveRuleResolution | null {
  if (axis === "sts") {
    for (let k = wi - 1; k >= 0; k--) {
      const candidate = weeks[k]?.sets?.[si];
      const rule = candidate?.progressionRule;
      const offset = wi - k;
      const apply = rule?.applyForNextWeeks ?? 1;
      if (rule && offset <= apply && ruleHasInheritableBody(rule)) {
        return { rule, sourceWi: k, sourceSi: si };
      }
    }
    return null;
  }
  // axis === "sis"
  const week = weeks[wi];
  if (!week) return null;
  for (let k = si - 1; k >= 0; k--) {
    const candidate = week.sets?.[k];
    const rule = candidate?.progressionRule;
    const offset = si - k;
    const apply = rule?.applyForNextSets ?? 1;
    if (rule && offset <= apply && ruleHasInheritableBody(rule)) {
      return { rule, sourceWi: wi, sourceSi: k };
    }
  }
  return null;
}

/**
 * Convenience predicate exported for consumers that want to know whether a
 * slot's stored rule is empty (so they can decide between using it directly
 * vs. consulting `resolveEffectiveRule`). Mirrors the in-file
 * `ruleHasOwnContent` semantics.
 */
export function ruleIsEmpty(rule: PrecompiledWeekProgressionRule | null | undefined): boolean {
  return !ruleHasOwnContent(rule);
}

// ─── PROG-ENGINE-V3/1 — per-axis source-slot resolver ────────────────────────
//
// Architect plan #4ccf1976 §2. Closes the bug where a non-empty own rule that
// only carries a dispatch hint (e.g. `loadComputeSource="sis"`) skipped
// walk-back at the dispatch site and missed the covering ancestor's rule. The
// gating predicate `ruleIsEmpty` is the wrong axis: "this slot has its own
// dispatch" and "the source-axis rule lives N slots back via applyForNext*"
// are independent concerns. `resolveSourceSlot` decouples them.
//
// Resolution order (per axis):
//   1. Explicit pin from the slot's own rule (`loadComputeSourceRef`) —
//      reserved for PROG-ENGINE-V3/2. Currently a no-op until the field is
//      added to `PrecompiledWeekProgressionRule`.
//   2. Walk-back via `resolveEffectiveRule` — returns the nearest ancestor
//      whose `applyForNext*` window covers the current offset.
//   3. Immediate prev slot fallback — preserves legacy behavior when no
//      ancestor covers (offset = 1, source rule may itself be empty).
//
// Returns null only when the axis has no prev slot at all (axis="sts" and
// wi===0, or axis="sis" and si===0).

export interface SourceSlotResolution {
  sourceWi: number;
  sourceSi: number;
  rule: PrecompiledWeekProgressionRule | null;
  sourceOffset: number;
}

export function resolveSourceSlot(
  weeks: PrecompiledWeek[],
  wi: number,
  si: number,
  axis: "sts" | "sis",
  ownRule?: PrecompiledWeekProgressionRule | null,
): SourceSlotResolution | null {
  // (1) Explicit pin from the slot's own rule (V3/2). Honored only when the
  //     pin's axis matches the requested axis AND the pin's loadComputeSource
  //     selection is consistent (mismatch → ignore + dev log). If the
  //     resolved slot doesn't exist (e.g. user deleted an intermediate set
  //     after pinning), fall through to default resolution + dev log; the
  //     field is preserved in storage so re-adding the slot restores intent.
  const pin = ownRule?.loadComputeSourceRef;
  if (pin && pin.axis === axis && pin.sourceOffset > 0) {
    const lcs = ownRule?.loadComputeSource;
    const axisMismatch = lcs != null && lcs !== "both" && lcs !== pin.axis;
    if (axisMismatch) {
      // eslint-disable-next-line no-console
      console.warn(
        "[resolveSourceSlot] loadComputeSourceRef.axis does not match loadComputeSource; ignoring pin",
        { wi, si, axis, pin, loadComputeSource: lcs },
      );
    } else {
      let pinned: PrecompiledSet | undefined;
      let pinnedWi = wi;
      let pinnedSi = si;
      if (axis === "sts") {
        pinnedWi = wi - pin.sourceOffset;
        pinned = weeks[pinnedWi]?.sets?.[si];
      } else {
        pinnedSi = si - pin.sourceOffset;
        pinned = weeks[wi]?.sets?.[pinnedSi];
      }
      if (pinned) {
        return {
          sourceWi: pinnedWi,
          sourceSi: pinnedSi,
          rule: pinned.progressionRule ?? null,
          sourceOffset: pin.sourceOffset,
        };
      }
      // eslint-disable-next-line no-console
      console.warn(
        "[resolveSourceSlot] loadComputeSourceRef points to a slot that no longer exists; falling back to default",
        { wi, si, axis, pin },
      );
    }
  }
  // (2) Walk-back via covering ancestor.
  const eff = resolveEffectiveRule(weeks, wi, si, axis);
  if (eff) {
    return {
      sourceWi: eff.sourceWi,
      sourceSi: eff.sourceSi,
      rule: eff.rule,
      sourceOffset: axis === "sts" ? wi - eff.sourceWi : si - eff.sourceSi,
    };
  }
  // (3) Immediate-prev fallback.
  if (axis === "sts" && wi > 0) {
    const src = weeks[wi - 1]?.sets?.[si];
    if (src) {
      return {
        sourceWi: wi - 1,
        sourceSi: si,
        rule: src.progressionRule ?? null,
        sourceOffset: 1,
      };
    }
  }
  if (axis === "sis" && si > 0) {
    const src = weeks[wi]?.sets?.[si - 1];
    if (src) {
      return {
        sourceWi: wi,
        sourceSi: si - 1,
        rule: src.progressionRule ?? null,
        sourceOffset: 1,
      };
    }
  }
  return null;
}

// ─── PROG-ENGINE-V3/3 — source-candidate enumeration for multi-source picker ─
//
// Architect plan #4ccf1976 §3. Produces the list of eligible source slots
// that the Sorgente carico Select renders, per-axis, when the user wants to
// disambiguate "inherit from immediate prev" vs "inherit from N-back covering
// ancestor". The picker writes the chosen `loadComputeSourceRef` onto the
// slot's rule; the resolver (resolveSourceSlot pin branch) honors it.
//
// Eligibility (per axis):
//   - offset = 1 (immediate prev): always eligible, even if the prev's rule
//     is empty — it's the legacy default fallback and must remain visible.
//   - offset > 1: eligible iff `offset <= applyForNext*` AND
//     `ruleHasOwnContent(rule)` (a consumer-only ancestor doesn't actually
//     contribute a rule body for descendants to inherit).
//
// `isDefault` is set on the candidate that `resolveSourceSlot(..., null)`
// would pick — the picker renders "(consigliato)" next to that label. The
// fourth `ownRule` arg is passed as `null` so the default reflects the
// implicit pick (no pin influence on the recommendation).

export interface SourceCandidate {
  axis: "sts" | "sis";
  sourceWi: number;
  sourceSi: number;
  sourceOffset: number;
  rule: PrecompiledWeekProgressionRule | null;
  /** True for the candidate that `resolveSourceSlot(weeks, wi, si, axis, null)` would pick. */
  isDefault: boolean;
  /** `ruleHasOwnContent(rule)` — UI may grey out empty ancestors. */
  hasContent: boolean;
  /** Pre-rendered human label (≤60 chars), suffixed "(consigliato)" when isDefault. */
  label: string;
  /** Full untruncated label for the tooltip on hover (Q1 in plan). */
  labelFull: string;
}

const MAX_LABEL_CHARS = 60;

function truncateLabel(s: string, max = MAX_LABEL_CHARS): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * Build a human-readable label for a source candidate. Italian copy to match
 * the rest of the editor UI ("Sorgente carico", "Set in Set", "Settimana N").
 * Includes a compact rule summary (e.g. "-5kg, propaga 2") when available.
 */
export function formatSourceLabel(
  rule: PrecompiledWeekProgressionRule | null,
  sourceWi: number,
  sourceSi: number,
  sourceOffset: number,
  axis: "sts" | "sis",
): { label: string; labelFull: string } {
  const axisLabel = axis === "sts" ? "Set to Set" : "Set in Set";
  // Slot anchor: cross-week candidates show "Settimana N · Set M", same-week
  // candidates just "Set M". sourceSi is 0-indexed; show 1-indexed to the user.
  const slotAnchor = axis === "sts"
    ? `Settimana ${sourceWi + 1} · Set ${sourceSi + 1}`
    : `Set ${sourceSi + 1}`;

  // Compact rule summary. Empty-rule offset=1 fallback gets a distinguishing tag.
  const hasContent = ruleHasOwnContent(rule);
  let summary = "";
  if (!hasContent) {
    summary = sourceOffset === 1 ? "immediato prev, nessuna regola" : "nessuna regola";
  } else if (rule) {
    const parts: string[] = [];
    const axisType = axis === "sts" ? rule.type : (rule.typeSiS ?? rule.type);
    const wp = axis === "sts" ? rule.weekPriority : rule.weekPrioritySiS;
    const changeType = axis === "sts" ? rule.loadChangeType : rule.loadChangeTypeSiS;
    const changeValue = axis === "sts" ? rule.loadChangeValue : rule.loadChangeValueSiS;
    if (axisType === "thresholds") {
      parts.push("soglie");
    } else if (changeValue != null) {
      const sign = changeValue > 0 ? "+" : "";
      const unit = changeType === "percentage" ? "%" : "kg";
      const mod = wp === "fixed_increment" ? " fissi" : wp === "wn_rm" ? " (wn_rm)" : "";
      parts.push(`${sign}${changeValue}${unit}${mod}`);
    } else if (wp === "wn_rm") {
      parts.push("wn_rm");
    } else if (wp === "none") {
      parts.push("nessuna progressione");
    }
    const apply = axis === "sts" ? rule.applyForNextWeeks : rule.applyForNextSets;
    if (apply != null && apply > 1) parts.push(`propaga ${apply}`);
    summary = parts.join(", ");
  }
  const summaryPart = summary ? ` (${summary})` : "";
  const labelFull = `${slotAnchor} · ${axisLabel}${summaryPart}`;
  return { label: truncateLabel(labelFull), labelFull };
}

export function enumerateSourceCandidates(
  weeks: PrecompiledWeek[],
  wi: number,
  si: number,
  axis: "sts" | "sis",
): SourceCandidate[] {
  const out: SourceCandidate[] = [];
  if (axis === "sts") {
    for (let k = wi - 1; k >= 0; k--) {
      const src = weeks[k]?.sets?.[si];
      if (!src) continue;
      const rule = src.progressionRule ?? null;
      const offset = wi - k;
      const hasContent = ruleHasOwnContent(rule);
      const apply = rule?.applyForNextWeeks ?? 1;
      const eligible = offset === 1 || (offset <= apply && hasContent);
      if (!eligible) continue;
      const { label, labelFull } = formatSourceLabel(rule, k, si, offset, axis);
      out.push({
        axis,
        sourceWi: k,
        sourceSi: si,
        sourceOffset: offset,
        rule,
        isDefault: false,
        hasContent,
        label,
        labelFull,
      });
    }
  } else {
    for (let k = si - 1; k >= 0; k--) {
      const src = weeks[wi]?.sets?.[k];
      if (!src) continue;
      const rule = src.progressionRule ?? null;
      const offset = si - k;
      const hasContent = ruleHasOwnContent(rule);
      const apply = rule?.applyForNextSets ?? 1;
      const eligible = offset === 1 || (offset <= apply && hasContent);
      if (!eligible) continue;
      const { label, labelFull } = formatSourceLabel(rule, wi, k, offset, axis);
      out.push({
        axis,
        sourceWi: wi,
        sourceSi: k,
        sourceOffset: offset,
        rule,
        isDefault: false,
        hasContent,
        label,
        labelFull,
      });
    }
  }
  // Mark default = the candidate `resolveSourceSlot(..., null)` would pick
  // (implicit recommendation; pin influence excluded so the "(consigliato)"
  // marker reflects the engine's natural choice, not the user's override).
  const def = resolveSourceSlot(weeks, wi, si, axis, null);
  if (def) {
    for (const c of out) {
      if (c.sourceWi === def.sourceWi && c.sourceSi === def.sourceSi) {
        c.isDefault = true;
        c.label = truncateLabel(`${c.labelFull} (consigliato)`);
        break;
      }
    }
  }
  return out;
}

// ─── PROG-ENGINE-V2/3 (Bug 3) — shared computeHypLoad + dedupe ───────────────
//
// Architect plan #72a6c050 §3 Bug 3 + Q3=yes (dedupe). Moved verbatim from
// PrecompiledProgressionForm.tsx's local `computeHypLoad`. Switch now reads
// `effectiveLoadComputeSource(rule)` so a fully-configured StS/SiS/Both rule
// with a null explicit `loadComputeSource` still dispatches via the
// progressionType-derived branch, instead of hitting the default `return null`
// and starving the badge until save+reopen.

/**
 * Project the hypothetical load for a slot. Mirrors the structure that lived
 * on the library page: initial baseline for (week0, set0), then per-source
 * dispatch via threshold-inherit-then-fixed/wn_rm fallback. The first-week
 * non-first-set case (only "sis"/"fixed"/"dynamic" applicable) and the
 * non-first-week first-set case (only "sts"/"fixed"/"dynamic") fall out
 * naturally — the prev* hooks for an unavailable axis arrive as nulls and the
 * inner inherit fns short-circuit to null.
 */
export function computeHypLoad(
  rule: PrecompiledWeekProgressionRule,
  isFirstWeek: boolean,
  setIndex: number,
  prevWeekHypLoad?: number | null,
  prevWeekSetRule?: PrecompiledWeekProgressionRule | null,
  prevSetHypLoad?: number | null,
  prevSetRule?: PrecompiledWeekProgressionRule | null,
  prevWeekSet?: PrecompiledSet | null,
  prevSetInWeek?: PrecompiledSet | null,
  weekRepScheme?: string | null,
): number | null {
  if (isFirstWeek && setIndex === 0) {
    return computeInitialBaseline(rule);
  }
  const stsInherit = (): number | null => {
    if (prevWeekSet) {
      // PROG-ENGINE-V2/4 — thread prevWeekHypLoad so uncompleted prev slots
      // materialize through the projection grid (per AC1.3).
      const t = applyRuleThresholdInherit(prevWeekSet, weekRepScheme, "sts", prevWeekHypLoad);
      if (t != null) return t;
    }
    return (prevWeekHypLoad != null && prevWeekSetRule != null)
      ? applyRuleStS(prevWeekHypLoad, prevWeekSetRule) : null;
  };
  const sisInherit = (): number | null => {
    if (prevSetInWeek) {
      const t = applyRuleThresholdInherit(prevSetInWeek, weekRepScheme, "sis", prevSetHypLoad);
      if (t != null) return t;
    }
    return (prevSetHypLoad != null && prevSetRule != null)
      ? applyRuleSiS(prevSetHypLoad, prevSetRule) : null;
  };
  switch (effectiveLoadComputeSource(rule)) {
    case "fixed": return rule.initialLoad ?? null;
    case "dynamic":
      if (rule.initialWnRm != null && rule.initialPnRm != null)
        return Math.round(rule.initialWnRm * rule.initialPnRm / 100 * 100) / 100;
      return null;
    case "sts":
      return stsInherit();
    case "sis":
      return sisInherit();
    case "both": {
      const stsLoad = stsInherit();
      const sisLoad = sisInherit();
      if (rule.calculationPriority === "sis") return sisLoad ?? stsLoad;
      return stsLoad ?? sisLoad;
    }
    default:
      return null;
  }
}

// ─── PROG-BUGS/ENGINE-3 (Bug A) — header reps pill coherence ─────────────────
//
// Architect plan #5f4be964 §1.A. The library page's SetMiniCard previously
// derived the header reps pill from a local `computeHypReps(weekRepScheme)`
// that just stripped a leading `\d+x` from the week's repScheme — completely
// independent of the per-set progression rule. Result: header pill and the
// "Ipotetico (da sorgente)" reps badge in the expanded body (driven by
// `computePrevSlotProjection` → `applyRuleThresholdInheritReps` /
// `applyRuleStSReps` / `applyRuleSiSReps`) diverged whenever the rule changed
// reps (threshold-with-repsValue, weekPriority=fixed_increment with
// incrementTarget=reps|both).
//
// `computeHypReps` mirrors `computeHypLoad` so both the header pill and the
// body badge come from the same engine path. Fallback to the week-level
// `repScheme` (stripped of the leading `\d+x` set-count) only when the
// rule-driven projection is null — preserving the legacy display for
// load-only rules and source slots.
//
// `computeHypRepsGrid` mirrors `computeHypLoadGrid` (and the library page's
// `weekHypLoads` memo at PrecompiledProgressionForm.tsx ~2040), including
// the ENGINE-2 `resolveEffectiveRule` walk-back so multi-iter source rules
// propagate reps the same way they propagate load.

// Function declaration (not const arrow) so it is hoisted and usable by
// callers above this point in the module (e.g. PROG-ENGINE-V2/2 Bug 2 fix
// in `applyRuleThresholdInheritReps`).
function stripSetCountPrefix(s: string | null | undefined): string | null {
  if (!s) return null;
  const trimmed = s.trim().replace(/^\d+[xX]\s*/i, "");
  return trimmed || null;
}

/**
 * Mirror of `computeHypLoad` for the reps projection. Threshold-inherit first
 * (via `applyRuleThresholdInheritReps`), then FE-F REPLACE fallback (via
 * `applyRuleStSReps` / `applyRuleSiSReps`). Returns null when no projection
 * applies — callers may then fall back to the week-level repScheme.
 */
export function computeHypReps(
  rule: PrecompiledWeekProgressionRule,
  isFirstWeek: boolean,
  setIndex: number,
  prevWeekHypReps: number | string | null,
  prevSetHypReps: number | string | null,
  prevWeekSet: PrecompiledSet | null,
  prevSetInWeek: PrecompiledSet | null,
  prevWeekSetRule: PrecompiledWeekProgressionRule | null,
  prevSetRule: PrecompiledWeekProgressionRule | null,
  weekRepScheme?: string | null,
  // PROG-ENGINE-V2/4 (#f38a51f4) — optional hyp-load grid values for the
  // matching prev slot. Forwarded to the threshold-inherit reps helper so an
  // uncompleted prev slot can materialize through the engine projection
  // (per AC1.3). Defaults to undefined for back-compat with legacy 10-arg
  // callers.
  prevWeekHypLoad?: number | null,
  prevSetHypLoad?: number | null,
): number | string | null {
  // Source slot: reps come from the week's repScheme (no rule-driven
  // projection has fired yet). Returning null lets the caller fall back.
  if (isFirstWeek && setIndex === 0) return null;
  const stsInherit = (): number | string | null => {
    if (prevWeekSet) {
      const t = applyRuleThresholdInheritReps(prevWeekSet, weekRepScheme, "sts", prevWeekHypLoad);
      if (t != null) return t;
    }
    if (prevWeekSetRule != null) {
      return applyRuleStSReps(
        typeof prevWeekHypReps === "number" ? prevWeekHypReps : 0,
        prevWeekSetRule,
      );
    }
    return null;
  };
  const sisInherit = (): number | string | null => {
    if (prevSetInWeek) {
      const t = applyRuleThresholdInheritReps(prevSetInWeek, weekRepScheme, "sis", prevSetHypLoad);
      if (t != null) return t;
    }
    if (prevSetRule != null) {
      return applyRuleSiSReps(
        typeof prevSetHypReps === "number" ? prevSetHypReps : 0,
        prevSetRule,
      );
    }
    return null;
  };
  switch (effectiveLoadComputeSource(rule)) {
    case "sts":
      return stsInherit();
    case "sis":
      return sisInherit();
    case "both": {
      const stsReps = stsInherit();
      const sisReps = sisInherit();
      if (rule.calculationPriority === "sis") return sisReps ?? stsReps;
      return stsReps ?? sisReps;
    }
    // fixed / dynamic / null: reps come from the week's repScheme, not the
    // rule — let the caller fall back.
    default:
      return null;
  }
}

/**
 * Returns a (weekIdx × setIdx) grid of hypothetical reps projections. Mirrors
 * `computeHypLoadGrid` — same ENGINE-2 walk-back for empty slots so multi-
 * iter source rules propagate reps as well as load.
 */
export function computeHypRepsGrid(
  weeks: PrecompiledWeek[],
): (number | string | null)[][] {
  const result: (number | string | null)[][] = [];
  weeks.forEach((week, wi) => {
    const row: (number | string | null)[] = [];
    const sets = week.sets ?? [];
    sets.forEach((set, si) => {
      // #615019e1 — align with `weekHypLoads` (PrecompiledProgressionForm.tsx
      // and ExerciseProgressionEditor.tsx `computeHypLoadGrid`): use the V3
      // `resolveSourceSlot` so walk-back fires even when the destination has
      // consumer-only own content (e.g. `loadComputeSource:"sis"`). The
      // prior `ruleIsEmpty` gate on `resolveEffectiveRule` made the reps
      // grid blind to walk-back as soon as the destination acquired any
      // rule body, so the dispatch read the intermediate's own rule
      // instead of the source's covering rule.
      const stsRes = resolveSourceSlot(weeks, wi, si, "sts", set.progressionRule);
      const sisRes = resolveSourceSlot(weeks, wi, si, "sis", set.progressionRule);
      let effectiveRule = set.progressionRule;
      const stsHasInheritedContent =
        stsRes != null && stsRes.rule != null && !ruleIsEmpty(stsRes.rule);
      const sisHasInheritedContent =
        sisRes != null && sisRes.rule != null && !ruleIsEmpty(sisRes.rule);
      if (
        ruleIsEmpty(set.progressionRule) &&
        (stsHasInheritedContent || sisHasInheritedContent)
      ) {
        const base =
          (stsHasInheritedContent ? stsRes!.rule : null) ??
          (sisHasInheritedContent ? sisRes!.rule : null) ??
          set.progressionRule;
        effectiveRule = { ...base };
        if (stsHasInheritedContent && sisHasInheritedContent) {
          effectiveRule.loadComputeSource = "both";
        } else if (stsHasInheritedContent) {
          effectiveRule.loadComputeSource = "sts";
        } else if (sisHasInheritedContent) {
          effectiveRule.loadComputeSource = "sis";
        }
      }
      const prevWeekReps = wi > 0 ? result[wi - 1]?.[si] ?? null : null;
      const prevWeekSetRaw = wi > 0 ? weeks[wi - 1].sets?.[si] ?? null : null;
      const prevSetReps = si > 0 ? row[si - 1] ?? null : null;
      const prevSetInWeekRaw = si > 0 ? sets[si - 1] ?? null : null;
      // PROG-BUGS/ENGINE-2-FIX — synthesize a prev-set view that carries the
      // effective walk-back rule, used by threshold-inherit + REPLACE
      // fallback when depth > 1.
      const stsDepth = stsRes?.sourceOffset ?? 0;
      const sisDepth = sisRes?.sourceOffset ?? 0;
      const stsSourceSet =
        stsRes ? weeks[stsRes.sourceWi]?.sets?.[stsRes.sourceSi] ?? null : null;
      const sisSourceSet =
        sisRes ? weeks[sisRes.sourceWi]?.sets?.[sisRes.sourceSi] ?? null : null;
      const synthPrevReps = (
        baseSet: PrecompiledSet | null,
        sourceSet: PrecompiledSet | null,
        chainReps: number | string | null,
        offset: number,
      ): PrecompiledSet | null => {
        // #615019e1 (second-order) — anchor `workReps` to the source seed
        // (parallel to #cda06c24 / #6644d7f6's load-axis fix) ONLY when the
        // walk-back offset is > 1. At offset=1 the immediate-prev chain
        // reps still tracks the source-adjacent value, so legacy behavior
        // is preserved by falling back to `chainReps` first. At offset>1
        // chainReps reflects the previous projection link which drifts the
        // threshold-band lookup in `materializePrevSlot`; using
        // `sourceSet.workReps ?? sourceSet.repScheme` keeps band
        // evaluation stable across every covered offset.
        const sourceReps: number | string | null =
          offset > 1
            ? (sourceSet?.workReps ?? sourceSet?.repScheme ?? null)
            : null;
        const repsRaw: number | string | null = sourceReps ?? chainReps;
        if (repsRaw == null) return baseSet;
        const wr =
          typeof repsRaw === "number" ? repsRaw : Number(repsRaw);
        if (!Number.isFinite(wr) || wr <= 0) return baseSet;
        // Carry source's workLoad/repScheme so the engine has a non-zero
        // baseLoad; override workReps (the source-anchored value) and
        // progressionRule (the effective walk-back rule).
        const baseWorkLoad: number =
          sourceSet?.workLoad ?? baseSet?.workLoad ?? 1;
        const stub: PrecompiledSet = baseSet ?? {
          label: null,
          trialLoad: null,
          progressionRule: null as any,
        };
        return {
          ...stub,
          workReps: wr,
          workLoad: baseWorkLoad,
          repScheme: sourceSet?.repScheme ?? baseSet?.repScheme ?? null,
          progressionRule: effectiveRule ?? baseSet?.progressionRule ?? stub.progressionRule,
        };
      };
      const prevWeekSet =
        stsDepth > 1
          ? synthPrevReps(prevWeekSetRaw, stsSourceSet, prevWeekReps, stsDepth)
          : prevWeekSetRaw;
      const prevSetInWeek =
        sisDepth > 1
          ? synthPrevReps(prevSetInWeekRaw, sisSourceSet, prevSetReps, sisDepth)
          : prevSetInWeekRaw;
      // #615019e1 — route the rule arg to the covering source's rule
      // whenever a source is resolved (mirrors `weekHypLoads` in
      // PrecompiledProgressionForm.tsx). The prior
      // `stsDepth > 1 ? effectiveRule : prevWeekSetRaw.rule` ternary
      // missed the offset=1 walk-back path, so the dispatch read the
      // intermediate's own rule at depth 1 — same family of bug as the
      // pre-#cda06c24 load anchor.
      const prevWeekSetRule =
        stsRes?.rule ?? prevWeekSetRaw?.progressionRule ?? null;
      const prevSetRule =
        sisRes?.rule ?? prevSetInWeekRaw?.progressionRule ?? null;
      row.push(
        computeHypReps(
          effectiveRule,
          wi === 0,
          si,
          prevWeekReps,
          prevSetReps,
          prevWeekSet,
          prevSetInWeek,
          prevWeekSetRule,
          prevSetRule,
          week.repScheme,
        ),
      );
    });
    result.push(row);
  });
  return result;
}

/**
 * Convenience: rule-driven reps projection if present, otherwise the week-
 * level repScheme stripped of its `^\d+x` set-count prefix. Mirrors the
 * legacy display path used by the library page's header pill.
 */
export function hypRepsDisplay(
  projected: number | string | null,
  weekRepScheme: string | null | undefined,
): number | string | null {
  if (projected != null) return projected;
  return stripSetCountPrefix(weekRepScheme);
}

